import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import Select, { components } from 'react-select';
import DownloadIcon from '@mui/icons-material/Download';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SalesPerformanceReportController from 'pages/Controller/SalesPerformanceReportController';
import Loader from 'utils/Loader';
import ZoomWrapper from 'utils/ZoomWrapper';

function AgencyPerformanceReport() {
  const {
    data,
    cloading,
    totalRecords,
    downloadLoading,
    handleClearFilters,
    handleDownloadReport,
    handleOpenDetails,
    zoneOptions
  } = SalesPerformanceReportController();

  const [area, setArea] = useState([]);
  const [month, setMonth] = useState('JUN-25');
  const [division, setDivision] = useState('ALL');

  const areaSelectOptions = useMemo(
    () => [
      { label: 'Select All', value: '__ALL__', isSelectAll: true },
      ...(zoneOptions.length ? zoneOptions : [
        { value: 'PATNA', label: 'PATNA' },
        { value: 'BANGALORE', label: 'BANGALORE' },
        { value: 'MUMBAI', label: 'MUMBAI' }
      ])
    ],
    [zoneOptions]
  );

  const allAreasSelected = useMemo(
    () => area.length > 0 && area.length === areaSelectOptions.length - 1,
    [area, areaSelectOptions]
  );

  useEffect(() => {
    if (area.length === 0 && areaSelectOptions.length > 1) {
      setArea(areaSelectOptions.filter((option) => option.value !== '__ALL__'));
    }
  }, [area, areaSelectOptions]);

  const handleAreaChange = (selected, actionMeta) => {
    if (actionMeta?.action === 'clear') {
      return;
    }

    if (!selected) {
      setArea([]);
      return;
    }

    if (selected.some((o) => o.value === '__ALL__')) {
      const allSelected = area.length === areaSelectOptions.length - 1;
      setArea(allSelected ? [] : areaSelectOptions.filter((o) => o.value !== '__ALL__'));
      return;
    }

    setArea(selected);
  };

  const CheckboxOption = (props) => {
    const { label, isSelectAll } = props.data;
    const { isSelected, selectOption } = props;
    const allSelected = Boolean(props.selectProps?.allItemsSelected);
    const checked = isSelectAll ? allSelected : isSelected;

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      selectOption(props.data);
    };

    return (
      <button
        type="button"
        onMouseDown={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 10px',
          width: '100%',
          background: '#fff',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        <input type="checkbox" checked={checked} readOnly />
        <span style={{ marginLeft: 8, color: '#0F172A' }}>{label}</span>
      </button>
    );
  };

  const NoMultiValue = () => null;

  const monthOptions = [
    { value: 'JUN-25', label: 'JUN-25' },
    { value: 'MAY-25', label: 'MAY-25' },
    { value: 'APR-25', label: 'APR-25' }
  ];

  const divisionOptions = [
    { value: 'ALL', label: 'ALL' },
    { value: 'SUIT', label: 'SUIT' },
    { value: 'SH-COT', label: 'SH-COT' }
  ];

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: 38,
      height: 38,
      borderRadius: 8,
      borderColor: '#D1D5DB',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#9CA3AF'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      height: 38,
      padding: '0 8px'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 38
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6B7280',
      fontWeight: 500
    })
  };

  const summary = useMemo(() => {
    const saleMtr = data.reduce((acc, row) => acc + Number(row.salesPerformerQuantity || 0), 0);
    const saleVal = data.reduce((acc, row) => acc + Number(row.orderQuantity || 0), 0);
    return {
      saleMtr: saleMtr.toFixed(2),
      saleVal: saleVal.toFixed(0),
      lyGrowthVol: '-91%',
      lyGrowthVal: '-91%',
      netOutstanding: totalRecords ? totalRecords.toLocaleString() : '11,246'
    };
  }, [data, totalRecords]);

  return (
    <>
    {cloading && (
                    <div
                        className="overlay"
                        style={{
                            position: "fixed",
                            inset: 0,
                            width: "100vw",
                            height: "100vh",
                            zIndex: 14000,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column"
                        }}
                    >
                        <Loader />
                        <span style={{ marginTop: "8px" }}>Data loading...</span>
                    </div>
                )}
    <ZoomWrapper>
      <div style={{ display: 'block', position: 'relative', minHeight: '100vh'}}>
        <Box sx={{ mt: 8, p: { xs: 2, md: 1 }, }}>
          

          <Paper sx={{ p: 2, mb: 1, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                AGENCY DASHBOARD
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>
                One Page Performance Report
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ width: '100%', maxWidth: 440 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
                  AREA
                </Typography>
                <Select
                  isMulti
                  options={areaSelectOptions}
                  value={area}
                  onChange={handleAreaChange}
                  allItemsSelected={allAreasSelected}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label}
                  placeholder="SELECT AREA"
                  styles={selectStyles}
                  components={{ Option: CheckboxOption, MultiValue: NoMultiValue }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' }, gap: 1, width: '100%' }}>
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Last Updated : 01 Jun, 2026 10:30 AM
              </Typography>
              <IconButton
                onClick={handleDownloadReport}
                disabled={downloadLoading}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  width: 40,
                  height: 40,
                  boxShadow: 'none',
                  border: '1px solid #E6EBF5',
                  '&:hover': {
                    backgroundColor: '#F3F4F6'
                  }
                }}
              >
                {downloadLoading ? (
                  <CircularProgress size={18} sx={{ color: '#0F172A' }} />
                ) : (
                  <DownloadIcon fontSize="small" sx={{ color: '#0F172A' }} />
                )}
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
        {[
          { title: '26-27 SALE MTR', value: summary.saleMtr, caption: '5.6% ↑', bg: '#ECF3FF', color: '#1E3A8A', icon: InsertChartOutlinedIcon },
          { title: '26-27 SALE VAL', value: summary.saleVal, caption: '5.7% ↑', bg: '#E8F8F6', color: '#047857', icon: MonetizationOnOutlinedIcon },
          { title: 'LY GROWTH % (VOL)', value: summary.lyGrowthVol, caption: '', bg: '#F5F3FF', color: '#7C3AED', icon: TrendingUpOutlinedIcon },
          { title: 'LY GROWTH % (VAL)', value: summary.lyGrowthVal, caption: '', bg: '#FFF7ED', color: '#C2410C', icon: TrendingDownOutlinedIcon },
          { title: 'NET O/S AMT', value: summary.netOutstanding, caption: '', bg: '#FEF2F2', color: '#B91C1C', icon: AccountBalanceWalletOutlinedIcon }
        ].map((card) => {
          const CardIcon = card.icon;
          return (
            <Grid item xs={12} sm={6} md={2.4} key={card.title}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: card.bg,
                  border: `1px solid ${card.color}20`,
                  minHeight: 126,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: card.color, fontWeight: 700, maxWidth: 'calc(100% - 40px)' }}>
                    {card.title}
                  </Typography>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, backgroundColor: `${card.color}20`, display: 'grid', placeItems: 'center' }}>
                    <CardIcon sx={{ color: card.color, fontSize: 18 }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, color: '#0F172A' }}>
                    {card.value}
                  </Typography>
                  {card.caption ? (
                    <Typography variant="caption" sx={{ color: card.color, display: 'block', mt: 0.5 }}>
                      {card.caption}
                    </Typography>
                  ) : (
                    <Box sx={{ height: 16 }} />
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: '#EEF2FF', display: 'grid', placeItems: 'center' }}>
                <Typography sx={{ fontSize: 18 }}>📈</Typography>
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                SALES ANALYTICS
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
              MONTH
            </Typography>
            <Select
              options={monthOptions}
              value={monthOptions.find((option) => option.value === month)}
              onChange={(option) => setMonth(option?.value || '')}
              placeholder="SELECT MONTH"
              styles={selectStyles}
              menuPortalTarget={document.body}
              classNamePrefix="react-select"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
              DIVISION
            </Typography>
            <Select
              options={divisionOptions}
              value={divisionOptions.find((option) => option.value === division)}
              onChange={(option) => setDivision(option?.value || '')}
              placeholder="SELECT DIVISION"
              styles={selectStyles}
              menuPortalTarget={document.body}
              classNamePrefix="react-select"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          1. BUDGET VS ACHIEVEMENT
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E6EBF5', mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                <TableCell sx={{ fontWeight: 700 }}>DIVISION</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>BUDGET (MTR)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>26-27 SALE MTR</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>BUDGET (VAL)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>26-27 SALE VAL</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>ACH % (VOL)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>GAP % (VOL)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { division: '01 SUIT', budgetMtr: '80.24', saleMtr: '5.85', budgetVal: '23,394', saleVal: '1,613', ach: '7%', gap: '-93%' },
                { division: '02 CB', budgetMtr: '11.64', saleMtr: '0.36', budgetVal: '2,422', saleVal: '69', ach: '3%', gap: '-97%' },
                { division: '11 SH-COT', budgetMtr: '52.19', saleMtr: '3.16', budgetVal: '10,696', saleVal: '512', ach: '6%', gap: '-94%' },
                { division: '12 SH-LIN', budgetMtr: '7.98', saleMtr: '0.72', budgetVal: '4,603', saleVal: '398', ach: '9%', gap: '-91%' },
                { division: '13 BREEZY', budgetMtr: '16.27', saleMtr: '0.49', budgetVal: '2,955', saleVal: '95', ach: '3%', gap: '-97%' },
                { division: '15 VELARDI', budgetMtr: '44.76', saleMtr: '1.72', budgetVal: '6,763', saleVal: '242', ach: '4%', gap: '-96%' },
                { division: '21 DUET', budgetMtr: '54.77', saleMtr: '2.58', budgetVal: '5,561', saleVal: '269', ach: '5%', gap: '-95%' }
              ].map((row) => (
                <TableRow key={row.division} hover>
                  <TableCell>{row.division}</TableCell>
                  <TableCell align="right">{row.budgetMtr}</TableCell>
                  <TableCell align="right">{row.saleMtr}</TableCell>
                  <TableCell align="right">{row.budgetVal}</TableCell>
                  <TableCell align="right">{row.saleVal}</TableCell>
                  <TableCell align="right">{row.ach}</TableCell>
                  <TableCell align="right" sx={{ color: '#DC2626' }}>{row.gap}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#F8FBFF', fontWeight: 700 }}>
                <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>267.89</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>14.89</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>56,394</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>3,198</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>6%</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#DC2626' }}>-94%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#ECFDF5', border: '1px solid #D1FAE5' }}>
              <Typography variant="subtitle2" sx={{ color: '#047857', fontWeight: 700, mb: 1 }}>
                2. GROWTH OVER LAST YEAR (VOLUME)
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>LY Growth % (Vol)</Typography>
                <Typography sx={{ color: '#DC2626', fontWeight: 800 }}>-91%</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#D1FAE5' }}>
                    <TableCell>DIVISION</TableCell>
                    <TableCell align="right">24-25</TableCell>
                    <TableCell align="right">25-26</TableCell>
                    <TableCell align="right">26-27</TableCell>
                    <TableCell align="right">LY GROWTH %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { division: '01 SUIT', a: '50.37', b: '59.41', c: '5.85', growth: '-90%' },
                    { division: '02 CB', a: '9.14', b: '8.79', c: '0.36', growth: '-96%' },
                    { division: '11 SH-COT', a: '28.76', b: '36.94', c: '3.16', growth: '-91%' },
                    { division: '12 SH-LIN', a: '3.69', b: '4.78', c: '0.72', growth: '-85%' },
                    { division: '13 BREEZY', a: '10.87', b: '9.47', c: '0.49', growth: '-95%' },
                    { division: '15 VELARDI', a: '7.77', b: '19.34', c: '1.72', growth: '-91%' },
                    { division: '21 DUET', a: '32.04', b: '35.70', c: '2.58', growth: '-93%' }
                  ].map((row) => (
                    <TableRow key={row.division} hover>
                      <TableCell>{row.division}</TableCell>
                      <TableCell align="right">{row.a}</TableCell>
                      <TableCell align="right">{row.b}</TableCell>
                      <TableCell align="right">{row.c}</TableCell>
                      <TableCell align="right" sx={{ color: '#DC2626' }}>{row.growth}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#EFFCF0', fontWeight: 700 }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">142.64</TableCell>
                    <TableCell align="right">174.43</TableCell>
                    <TableCell align="right">14.89</TableCell>
                    <TableCell align="right" sx={{ color: '#DC2626' }}>-91%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7' }}>
              <Typography variant="subtitle2" sx={{ color: '#B45309', fontWeight: 700, mb: 1 }}>
                3. GROWTH OVER LAST YEAR (VALUE)
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>LY Growth % (Val)</Typography>
                <Typography sx={{ color: '#DC2626', fontWeight: 800 }}>-91%</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FEF3C7' }}>
                    <TableCell>DIVISION</TableCell>
                    <TableCell align="right">24-25</TableCell>
                    <TableCell align="right">25-26</TableCell>
                    <TableCell align="right">26-27</TableCell>
                    <TableCell align="right">LY GROWTH %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { division: '01 SUIT', a: '13,940', b: '16,712', c: '1,613', growth: '-90%' },
                    { division: '02 CB', a: '1,785', b: '1,760', c: '69', growth: '-96%' },
                    { division: '11 SH-COT', a: '5,766', b: '7,195', c: '512', growth: '-93%' },
                    { division: '12 SH-LIN', a: '2,128', b: '2,565', c: '398', growth: '-84%' },
                    { division: '13 BREEZY', a: '1,991', b: '1,727', c: '95', growth: '-94%' },
                    { division: '15 VELARDI', a: '1,139', b: '2,846', c: '242', growth: '-91%' },
                    { division: '21 DUET', a: '3,107', b: '3,493', c: '269', growth: '-92%' }
                  ].map((row) => (
                    <TableRow key={row.division} hover>
                      <TableCell>{row.division}</TableCell>
                      <TableCell align="right">{row.a}</TableCell>
                      <TableCell align="right">{row.b}</TableCell>
                      <TableCell align="right">{row.c}</TableCell>
                      <TableCell align="right" sx={{ color: '#DC2626' }}>{row.growth}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#FFFBEB', fontWeight: 700 }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">29,856</TableCell>
                    <TableCell align="right">36,298</TableCell>
                    <TableCell align="right">3,198</TableCell>
                    <TableCell align="right" sx={{ color: '#DC2626' }}>-91%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mt: 3, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
          <Typography variant="subtitle2" sx={{ color: '#4338CA', fontWeight: 700, mb: 2 }}>
            4. QUARTER WISE BREAKUP (CURRENT FINANCIAL YEAR ONLY)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                <TableCell>DIVISION</TableCell>
                <TableCell align="center">Q1 (%)</TableCell>
                <TableCell align="center">Q2 (%)</TableCell>
                <TableCell align="center">Q3 (%)</TableCell>
                <TableCell align="center">Q4 (%)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { division: '01 SUIT', q1: '7%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '02 CB', q1: '3%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '11 SH-COT', q1: '5%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '12 SH-LIN', q1: '9%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '13 BREEZY', q1: '3%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '15 VELARDI', q1: '4%', q2: '0%', q3: '0%', q4: '0%' },
                { division: '21 DUET', q1: '5%', q2: '0%', q3: '0%', q4: '0%' }
              ].map((row) => (
                <TableRow key={row.division} hover>
                  <TableCell>{row.division}</TableCell>
                  <TableCell align="center">{row.q1}</TableCell>
                  <TableCell align="center">{row.q2}</TableCell>
                  <TableCell align="center">{row.q3}</TableCell>
                  <TableCell align="center">{row.q4}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#F8FBFF', fontWeight: 700 }}>
                <TableCell>Grand Total</TableCell>
                <TableCell align="center">6%</TableCell>
                <TableCell align="center">0%</TableCell>
                <TableCell align="center">0%</TableCell>
                <TableCell align="center">0%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  5. DEALER PERFORMANCE (VALUE)
                </Typography>
              </Grid>
              <Grid item>
                <TextField size="small" placeholder="Search Dealer..." fullWidth sx={{ backgroundColor: '#F8FAFC', maxWidth: 240 }} />
              </Grid>
            </Grid>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                    <TableCell>#</TableCell>
                    <TableCell>DEALER NAME</TableCell>
                    <TableCell>CITY</TableCell>
                    <TableCell align="right">23-24 VAL</TableCell>
                    <TableCell align="right">24-25 VAL</TableCell>
                    <TableCell align="right">25-26 VAL</TableCell>
                    <TableCell align="right">26-27 VAL</TableCell>
                    <TableCell align="right">LY GROWTH %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { idx: 1, dealer: 'PAVITHRA FAB', city: 'BANGALORE', a: '66.35', b: '123.34', c: '80.87', d: '2.96', growth: '-96%' },
                    { idx: 2, dealer: 'CHHAJED TEXTILE', city: 'HUBLI', a: '155.60', b: '186.91', c: '191.45', d: '13.68', growth: '-93%' },
                    { idx: 3, dealer: 'VASSANT TEXTILE MILLS', city: 'BENGALURU', a: '54.91', b: '69.66', c: '106.78', d: '1.13', growth: '-93%' },
                    { idx: 4, dealer: 'BALAJI TEXTILES', city: 'DEOGHAR', a: '12.25', b: '4.19', c: '17.38', d: '2.40', growth: '-86%' },
                    { idx: 5, dealer: 'SITA TEXTILES', city: 'KATIHAR', a: '322.99', b: '335.08', c: '321.92', d: '39.04', growth: '-88%' }
                  ].map((row) => (
                    <TableRow key={row.dealer} hover>
                      <TableCell>{row.idx}</TableCell>
                      <TableCell>{row.dealer}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell align="right">{row.a}</TableCell>
                      <TableCell align="right">{row.b}</TableCell>
                      <TableCell align="right">{row.c}</TableCell>
                      <TableCell align="right">{row.d}</TableCell>
                      <TableCell align="right" sx={{ color: '#DC2626' }}>{row.growth}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3,borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 3 }}>
              6. PAYMENT & OUTSTANDING
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 2, mb: 3 }}>
              {[
                { label: 'G AMT', value: '12,662', bg: '#F0FDF4', color: '#166534' },
                { label: 'NET AMT', value: '11,246', bg: '#EFF6FF', color: '#1D4ED8' },
                { label: 'M47', value: '7,513', bg: '#FEF3C7', color: '#92400E' },
                { label: 'M60', value: '6,431', bg: '#FCE7F3', color: '#BE185D' },
                { label: 'M90', value: '4,331', bg: '#FEE2E2', color: '#991B1B' },
                { label: 'M180', value: '902', bg: '#EEF2FF', color: '#3730A3' },
                { label: 'M365', value: '859', bg: '#ECFEFF', color: '#155E75' }
              ].map((metric) => (
                <Box
                  key={metric.label}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: metric.bg,
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 88
                  }}
                >
                  <Typography variant="caption" sx={{ color: metric.color, fontWeight: 700, textAlign: 'center' }}>
                    {metric.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.75, color: '#0F172A' }}>
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E6EBF5' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                    <TableCell>#</TableCell>
                    <TableCell>AREA</TableCell>
                    <TableCell>SOLD-TO PARTY</TableCell>
                    <TableCell>SOLD-TO PARTY NAME</TableCell>
                    <TableCell align="right">25-26 VAL</TableCell>
                    <TableCell align="right">NET O/S VAL</TableCell>
                    <TableCell align="right">G AMT</TableCell>
                    <TableCell align="right">M47</TableCell>
                    <TableCell align="right">M60</TableCell>
                    <TableCell align="right">M90</TableCell>
                    <TableCell align="right">M180</TableCell>
                    <TableCell align="right">M365</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { idx: 1, area: 'MUZAFFARPUR', party: '5003962', name: 'LAXMI NARAYAN DINESH KUMAR', val: '512', outstanding: '339', gAmt: '345', m47: '272', m60: '234', m90: '203', m180: '148', m365: '0.83' },
                    { idx: 2, area: 'CALCUTTA', party: '5004057', name: 'J D SYNTHETICS', val: '326', outstanding: '285', gAmt: '296', m47: '186', m60: '186', m90: '164', m180: '147', m365: '60.61' },
                    { idx: 3, area: 'PATNA', party: '5004369', name: 'SHREE RAM FABRICS', val: '310', outstanding: '283', gAmt: '296', m47: '255', m60: '244', m90: '191', m180: '141', m365: '69.85' },
                    { idx: 4, area: 'MUZAFFARPUR', party: '5003080', name: 'AGRAWAL SYNTHETICS', val: '690', outstanding: '276', gAmt: '283', m47: '194', m60: '176', m90: '102', m180: '0', m365: '0.00' },
                    { idx: 5, area: 'GORAKHPUR', party: '5009429', name: 'AGRAWAL TEXTILES', val: '813', outstanding: '266', gAmt: '266', m47: '132', m60: '66', m90: '0', m180: '0', m365: '0.00' }
                  ].map((row, index) => (
                    <TableRow key={row.party} hover sx={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                      <TableCell>{row.idx}</TableCell>
                      <TableCell>{row.area}</TableCell>
                      <TableCell>{row.party}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right" sx={{ color: '#0F172A', fontWeight: 700 }}>{row.val}</TableCell>
                      <TableCell align="right" sx={{ color: '#DC2626', fontWeight: 700 }}>{row.outstanding}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#FEE2E2' }}>{row.gAmt}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#FEE2E2' }}>{row.m47}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#FEE2E2' }}>{row.m60}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#FEE2E2' }}>{row.m90}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#EEF2FF' }}>{row.m180}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#ECFEFF' }}>{row.m365}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#475569' }}>
                1 - 5 / 89
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ border: '1px solid #E6EBF5', color: '#0F172A' }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ border: '1px solid #E6EBF5', color: '#0F172A' }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  </div>
</ZoomWrapper>
</>
  );
}

export default AgencyPerformanceReport;
